import React, { useEffect, useState, useRef } from 'react'
import { useIntl } from 'react-intl'
import { Link, useLocation } from 'react-router-dom'
import { useManualQuery } from 'graphql-hooks'
import { Table, Tag, Space, Typography, Row, Col, Radio } from 'antd'
import type { TablePaginationConfig } from 'antd/es/table'
import type { FilterValue, SorterResult } from 'antd/es/table/interface'
import { FormOutlined } from '@ant-design/icons'
import isEqual from 'lodash/isEqual'

import { useAppSelector } from '../../../store/hooks'
import { selectAuthCompanySlice } from '../../../store/auth-company-slice'
import { getAllLeavePolicies, getLeaveTypesListAndLocations } from '../../../graphql/custom-queries'
import { invertHexColor } from '@vacationtracker/shared/components/utils/invert-color-wrapper'
import logger from '@vacationtracker/shared/functions/logger'
import { locationOptionsByCountry } from '@vacationtracker/shared/functions/location-country-groups'
import { leavePoliciesOverviewTracker } from './services/leavePoliciesTracking'

import IntlMessages from '../../../util/IntlMessages'
import { PageHeader } from '../../../design-system/PageHeader'
import CircularProgress from '../../../components/circular-progress'
import { FilterTriggerRow, FilterRowItem, FilterValues, useFilterRowLabels } from '../../../design-system/FilterTrigger'
import { getStorageKey, readStoredSelected, writeStoredSelected } from '@vacationtracker/shared/components/filter-advanced/storage'

import Api from '@vacationtracker/shared/services/api'
import { FrontendUrls } from '../../../types/urls'
import { useLocationAdmin } from '../../../hooks/use-location-admin'
import { IGetAllLeavePoliciesData, ILeavePolicyOverview } from '../../../types/leave-policies'
import { ILocationGeneralLeavePolicies } from '../../../types/locations'
import { ISelected } from '@vacationtracker/shared/components/filter-advanced/types'
import { ShortestLeaveIntervalEnum } from '@vacationtracker/shared/types/leave-policy'
import { Switch, App } from 'antd'

const { Text } = Typography

interface ILocationShort {
  id: string
  name: string
  countryCode?: string | null
}

interface ILeaveTypeShort {
  id: string
  name: string
  color: string
  position: number
  isActive: boolean
  deleted: boolean
}

interface IGetLeaveTypesAndLocationsData {
  getLocationList: ILocationShort[]
  getLeaveTypeList: ILeaveTypeShort[]
}

interface ILeavePoliciesFilter {
  locationIds: string[]
  leaveTypeIds: string[]
}

const LeavePoliciesOverviewPage = (): React.ReactElement => {
  const location = useLocation()
  const { formatMessage } = useIntl()
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { isLocationAdmin, managedLocationIds } = useLocationAdmin()
  const [loading, setLoading] = useState(true)
  const [leavePolicies, setLeavePolicies] = useState<ILeavePolicyOverview[]>([])
  const [filteredLeavePolicies, setFilteredLeavePolicies] = useState<ILeavePolicyOverview[]>([])
  const [locations, setLocations] = useState<ILocationShort[]>([])
  const [leaveTypes, setLeaveTypes] = useState<ILeaveTypeShort[]>([])
  // Same storage key the legacy FilterAdvanced used ('leavePoliciesFilters') - stored filters
  // carry over without migration. Undefined userId matches the previous caller (no user prefix).
  const filterStorageKey = getStorageKey('leavePolicies', undefined)
  const rowLabels = useFilterRowLabels()
  const [filters, setFilters] = useState<ILeavePoliciesFilter>(() => {
    const stored = readStoredSelected(filterStorageKey)
    const storedValues = (type: string) => stored.find((entry) => entry.type === type)?.values || []
    return { locationIds: storedValues('Locations'), leaveTypeIds: storedValues('Leave Types') }
  })
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const { notification } = App.useApp()

  // Add refs to prevent multiple calls
  const abortControllerRef = useRef<AbortController>(new AbortController())
  const isMountedRef = useRef<boolean>(true)
  const isFetchingRef = useRef<boolean>(false)

  const [getAllLeavePoliciesQuery] = useManualQuery<IGetAllLeavePoliciesData>(getAllLeavePolicies)
  const [getLeaveTypesAndLocationsQuery] = useManualQuery<IGetLeaveTypesAndLocationsData>(getLeaveTypesListAndLocations)

  useEffect(() => {
    // Set mounted flag
    isMountedRef.current = true

    // Initialize tracking
    leavePoliciesOverviewTracker.init()
    leavePoliciesOverviewTracker.trackSectionView('leave_policies_overview')

    // Initialize filters from URL parameters when the URL carries them; otherwise keep the
    // localStorage restore in place rather than wiping it with empty arrays (same bug fix the
    // CalendarPage got).
    if (isInitialLoad) {
      const urlParams = new URLSearchParams(location.search)
      const urlLocationIds = urlParams.get('locations')?.split(',').filter(Boolean)
      const urlLeaveTypeIds = urlParams.get('leaveTypes')?.split(',').filter(Boolean)
      const hasUrlFilters = Boolean(urlLocationIds || urlLeaveTypeIds)
      if (hasUrlFilters) {
        setFilters((prev) => ({
          locationIds: urlLocationIds ?? prev.locationIds,
          leaveTypeIds: urlLeaveTypeIds ?? prev.leaveTypeIds,
        }))
      }
    }

    // Fetch data only once when component mounts
    if (!isFetchingRef.current) {
      fetchInitialData()
    }

    // Cleanup function
    return () => {
      isMountedRef.current = false
      // Abort any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      // Cleanup tracking
      leavePoliciesOverviewTracker.cleanup()
    }
  }, [])

  // Sanitize restored filter IDs once option lists have loaded - a deleted/renamed location or
  // leave type from a prior session would otherwise linger as an active filter and the table
  // would narrow to empty with no hint why.
  useEffect(() => {
    if (locations.length === 0 && leaveTypes.length === 0) return
    const validLocationIds = new Set(locations.map((l) => l.id))
    const validLeaveTypeIds = new Set(leaveTypes.map((lt) => lt.id))
    const sanitized: ILeavePoliciesFilter = {
      locationIds: filters.locationIds.filter((id) => validLocationIds.has(id)),
      leaveTypeIds: filters.leaveTypeIds.filter((id) => validLeaveTypeIds.has(id)),
    }
    if (sanitized.locationIds.length === filters.locationIds.length && sanitized.leaveTypeIds.length === filters.leaveTypeIds.length) {
      return
    }
    const toStore = [
      { type: 'Locations', values: sanitized.locationIds },
      { type: 'Leave Types', values: sanitized.leaveTypeIds },
    ].filter((entry) => entry.values.length) as ISelected[]
    writeStoredSelected(filterStorageKey, toStore)
    setFilters(sanitized)
  }, [locations, leaveTypes, filters, filterStorageKey])

  useEffect(() => {
    if (!isInitialLoad && leavePolicies.length > 0) {
      applyFilters()
    }
  }, [filters, leavePolicies])

  useEffect(() => {
    if (leavePolicies.length > 0) {
      applyFilters()
    }
  }, [leavePolicies])

  const fetchInitialData = async () => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) {
      return
    }

    try {
      isFetchingRef.current = true
      setLoading(true)

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController()

      // Fetch both leave policies and filter data
      const [policiesResponse, filterDataResponse] = await Promise.all([
        getAllLeavePoliciesQuery({
          fetchOptionsOverrides: {
            signal: abortControllerRef.current.signal,
          },
        }),
        getLeaveTypesAndLocationsQuery({
          fetchOptionsOverrides: {
            signal: abortControllerRef.current.signal,
          },
        }),
      ])

      // Check if component is still mounted before updating state
      if (!isMountedRef.current) {
        return
      }

      if (!policiesResponse.data || policiesResponse.error) throw policiesResponse.error
      if (!filterDataResponse.data || filterDataResponse.error) throw filterDataResponse.error

      // Set filter data
      setLocations(filterDataResponse.data.getLocationList)
      setLeaveTypes(filterDataResponse.data.getLeaveTypeList.filter((lt) => lt.isActive && !lt.deleted))

      // Transform the leave policies data for the table
      const policiesData: ILeavePolicyOverview[] = []

      policiesResponse.data.getLocationList.forEach((location) => {
        location.leavePolicies.forEach((policy) => {
          if (policy.leaveType.isActive && !policy.leaveType.deleted) {
            policiesData.push({
              key: `${policy.locationId}-${policy.leaveType.id}`,
              id: policy.id,
              policyName: policy.leaveType.name,
              type: policy.leaveType.name,
              location: location.name,
              locationId: policy.locationId,
              leaveTypeId: policy.leaveType.id,
              leaveTypeColor: policy.leaveType.color,
              yearlyQuota: policy.hasUnlimitedDays
                ? formatMessage({ id: 'app.unlimited' })
                : `${authCompany?.hourlyLeaveAccounting ? policy.hoursPerYear : policy.daysPerYear} ${authCompany?.hourlyLeaveAccounting ? 'hours' : 'days'}`,
              approvalRequired: policy.isApprovalRequired,
              negativeBalance: policy.negativeBallanceAllowed,
              shortestDuration: policy.shortestLeaveInterval?.toLowerCase() || ShortestLeaveIntervalEnum.fullDay.toLowerCase(),
              isActive: policy.isActive,
              rawPolicy: policy as unknown as Partial<ILocationGeneralLeavePolicies>,
            })
          }
        })
      })

      setLeavePolicies(policiesData)
      setIsInitialLoad(false)

      // Track page view
      leavePoliciesOverviewTracker.trackViewed({
        totalPoliciesCount: policiesData.length,
      })
    } catch (error: unknown) {
      // Only log error if it's not an abort error and component is still mounted
      if ((error as Error).name !== 'AbortError' && isMountedRef.current) {
        logger.error('Error fetching data:', error)
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false)
        isFetchingRef.current = false
      }
    }
  }

  const applyFilters = () => {
    let filtered = [...leavePolicies]

    // Hide policies outside location admin scope
    if (isLocationAdmin && managedLocationIds.length > 0) {
      filtered = filtered.filter((policy) => managedLocationIds.includes(policy.locationId))
    }

    // Filter by location
    if (filters.locationIds.length > 0) {
      filtered = filtered.filter((policy) => filters.locationIds.includes(policy.locationId))
    }

    // Filter by leave type
    if (filters.leaveTypeIds.length > 0) {
      filtered = filtered.filter((policy) => filters.leaveTypeIds.includes(policy.leaveTypeId))
    }

    setFilteredLeavePolicies(filtered)

    // Update tracker with page data
    const activeFilters: string[] = []
    if (filters.locationIds.length > 0) {
      activeFilters.push(`locations:${filters.locationIds.length}`)
    }
    if (filters.leaveTypeIds.length > 0) {
      activeFilters.push(`leaveTypes:${filters.leaveTypeIds.length}`)
    }

    leavePoliciesOverviewTracker.updatePageData({
      totalPoliciesCount: leavePolicies.length,
      filteredPoliciesCount: filtered.length,
      activeFilters,
    })
  }

  const handleFilterChange = (data: Record<string, string[]>) => {
    let reject = true
    // Check if filters actually changed
    for (const [key] of Object.entries(data)) {
      const filterKey = key === 'locationIds' ? 'locationIds' : key === 'leaveTypeIds' ? 'leaveTypeIds' : key
      if (!isEqual(filters[filterKey], data[key])) {
        reject = false
      }
    }
    if (reject) return

    const { locationIds, leaveTypeIds } = data
    let url = location.pathname
    let searchParams = ''

    if (locationIds?.length) {
      searchParams += `&locations=${locationIds.join(',')}`
    }
    if (leaveTypeIds?.length) {
      searchParams += `&leaveTypes=${leaveTypeIds.join(',')}`
    }
    url += searchParams.replace('&', '?')
    window.history.pushState({}, '', url)

    // Persist so filters survive navigation away and back - the URL alone drops them.
    const toStore = [
      { type: 'Locations', values: locationIds || [] },
      { type: 'Leave Types', values: leaveTypeIds || [] },
    ].filter((entry) => entry.values?.length) as ISelected[]
    writeStoredSelected(filterStorageKey, toStore)

    setFilters({
      locationIds: locationIds || [],
      leaveTypeIds: leaveTypeIds || [],
    })
  }

  // Track filter changes after filters are applied
  useEffect(() => {
    if (!isInitialLoad && leavePolicies.length > 0) {
      const locationsCount = filters.locationIds.length
      const leaveTypesCount = filters.leaveTypeIds.length

      let filterType: 'location' | 'leave_type' | 'both' | 'cleared' = 'cleared'
      if (locationsCount > 0 && leaveTypesCount > 0) {
        filterType = 'both'
      } else if (locationsCount > 0) {
        filterType = 'location'
      } else if (leaveTypesCount > 0) {
        filterType = 'leave_type'
      }

      leavePoliciesOverviewTracker.trackFiltered({
        filterType,
        locationsSelected: locationsCount,
        leaveTypesSelected: leaveTypesCount,
        totalPoliciesCount: leavePolicies.length,
        filteredPoliciesCount: filteredLeavePolicies.length,
      })
    }
  }, [filters])

  const handleEditClick = (record: ILeavePolicyOverview, method: 'table_row_click' | 'edit_icon_click') => {
    leavePoliciesOverviewTracker.trackEdited({
      policyId: record.id,
      policyName: record.policyName,
      leaveTypeId: record.leaveTypeId,
      leaveTypeName: record.type,
      locationId: record.locationId,
      locationName: record.location,
      interactionMethod: method,
    })
  }

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ILeavePolicyOverview> | SorterResult<ILeavePolicyOverview>[]
  ) => {
    // Track pagination changes
    if (pagination.current && pagination.current !== currentPage) {
      leavePoliciesOverviewTracker.trackPaginated({
        interactionType: 'page_change',
        fromPage: currentPage,
        toPage: pagination.current,
      })
      setCurrentPage(pagination.current)
    }

    if (pagination.pageSize && pagination.pageSize !== pageSize) {
      leavePoliciesOverviewTracker.trackPaginated({
        interactionType: 'page_size_change',
        fromPageSize: pageSize,
        toPageSize: pagination.pageSize,
      })
      setPageSize(pagination.pageSize)
      setCurrentPage(1) // Reset to first page when page size changes
    }

    // Track sorting changes
    const sortResult = Array.isArray(sorter) ? sorter[0] : sorter
    if (sortResult.column) {
      leavePoliciesOverviewTracker.trackSorted({
        columnName: (sortResult.columnKey as string) || (sortResult.field as string),
        sortDirection: sortResult.order || null,
        totalPoliciesDisplayed: filteredLeavePolicies.length,
      })
    }
  }

  // Guard: map possible string to enum value, defaulting safely
  const toShortestInterval = (v?: unknown): ShortestLeaveIntervalEnum =>
    Object.values(ShortestLeaveIntervalEnum).includes(v as ShortestLeaveIntervalEnum) ? (v as ShortestLeaveIntervalEnum) : ShortestLeaveIntervalEnum.fullDay

  type EnableLeavePolicyBody = {
    eventType: 'LEAVE_POLICY_ENABLED'
    eventGroup: 'LEAVE_POLICY'
    leaveTypeId: string
    locationId: string
    daysPerYear?: number
    hoursPerYear?: number
    hasUnlimitedDays: boolean
    isApprovalRequired: boolean
    isReasonRequired: boolean
    negativeBallanceAllowed: boolean
    shortestLeaveInterval: ShortestLeaveIntervalEnum
    maxRolloverDays: number
    maxRolloverHours?: number
    quotaSetIn: 'days' | 'hours'
    hideLeaveType: boolean
    toil: boolean
    version: 3
  }

  const enableLeavePolicyFromOverview = async (leavePolicy: Partial<ILocationGeneralLeavePolicies>) => {
    const body: EnableLeavePolicyBody = {
      eventType: 'LEAVE_POLICY_ENABLED',
      eventGroup: 'LEAVE_POLICY',
      leaveTypeId: leavePolicy.leaveType?.id || (leavePolicy as any).leaveTypeId,
      locationId: leavePolicy.locationId as string,
      daysPerYear: leavePolicy.daysPerYear,
      hoursPerYear: leavePolicy.hoursPerYear,
      hasUnlimitedDays: Boolean(leavePolicy.hasUnlimitedDays),
      isApprovalRequired: Boolean(leavePolicy.isApprovalRequired),
      isReasonRequired: Boolean((leavePolicy as any).isReasonRequired),
      negativeBallanceAllowed: Boolean(leavePolicy.negativeBallanceAllowed),
      shortestLeaveInterval: toShortestInterval(leavePolicy.shortestLeaveInterval),
      maxRolloverDays: typeof leavePolicy.maxRolloverDays === 'number' ? leavePolicy.maxRolloverDays : 0,
      quotaSetIn: ((leavePolicy as any).quotaSetIn || 'days') as 'days' | 'hours',
      hideLeaveType: Boolean(leavePolicy.hideLeaveType),
      toil: Boolean(leavePolicy.toil),
      version: 3,
    }

    if (typeof leavePolicy.maxRolloverHours === 'number') {
      body.maxRolloverHours = leavePolicy.maxRolloverHours
    }

    return Api.post('/core/event', body)
  }

  const handleStatusToggle = async (record: ILeavePolicyOverview, checked: boolean) => {
    try {
      if (!checked) {
        // DISABLE
        await Api.post('/core/event', {
          eventType: 'LEAVE_POLICY_DISABLED',
          eventGroup: 'LEAVE_POLICY',
          leaveTypeId: record.leaveTypeId,
          locationId: record.locationId,
        })
      } else {
        // ENABLE
        const fullPolicy = record.rawPolicy as Partial<ILocationGeneralLeavePolicies> | undefined

        // Ensure required fields exist before posting
        const hasLocation = Boolean(fullPolicy?.locationId)
        const hasLeaveTypeId = Boolean(fullPolicy?.leaveType?.id || (fullPolicy as any)?.leaveTypeId)

        if (!fullPolicy || !hasLocation || !hasLeaveTypeId) {
          logger.error('Missing or incomplete rawPolicy for leave policy', {
            record,
            hasLocation,
            hasLeaveTypeId,
          })
          return
        }

        await enableLeavePolicyFromOverview(fullPolicy)
      }

      // Optimistic update
      setLeavePolicies((prev) => prev.map((p) => (p.id === record.id ? { ...p, isActive: checked } : p)))
    } catch (e: any) {
      logger.error('Error updating leave policy status', e?.response?.data || e)
      notification.error({
        message: formatMessage({ id: 'error.somethingWentWrong' }),
        description: String(e?.response?.data || e),
      })
    }
  }

  const columns = [
    {
      title: <IntlMessages id="app.status" />,
      dataIndex: 'isActive',
      key: 'status',
      align: 'center' as const,
      render: (isActive: boolean, record: ILeavePolicyOverview) => <Switch checked={isActive} onChange={(checked) => handleStatusToggle(record, checked)} />,
    },
    {
      title: <IntlMessages id="app.leavePoliciesName" />,
      dataIndex: 'policyName',
      key: 'policyName',
      sorter: (a: ILeavePolicyOverview, b: ILeavePolicyOverview) => a.policyName.localeCompare(b.policyName),
      render: (text: string, record: ILeavePolicyOverview) => (
        <Link to={`/app/settings/location/${record.locationId}/leave-policies/${record.leaveTypeId}`} onClick={() => handleEditClick(record, 'table_row_click')}>
          {text}
        </Link>
      ),
    },
    {
      title: <IntlMessages id="app.leaveType" />,
      dataIndex: 'type',
      key: 'type',
      sorter: (a: ILeavePolicyOverview, b: ILeavePolicyOverview) => a.type.localeCompare(b.type),
      render: (text: string, record: ILeavePolicyOverview) => (
        <Link to={FrontendUrls.leaveTypes}>
          <Tag color={record.leaveTypeColor}>
            <span style={{ color: invertHexColor(record.leaveTypeColor, true) }}>{text}</span>
          </Tag>
        </Link>
      ),
    },
    {
      title: <IntlMessages id="app.location" />,
      dataIndex: 'location',
      key: 'location',
      sorter: (a: ILeavePolicyOverview, b: ILeavePolicyOverview) => a.location.localeCompare(b.location),
      render: (text: string, record: ILeavePolicyOverview) => <Link to={`/app/settings/locations/${record.locationId}/leave-policies`}>{text}</Link>,
    },
    {
      title: <IntlMessages id="components.leavePolicyForm.yearlyQuota" />,
      dataIndex: 'yearlyQuota',
      key: 'yearlyQuota',
    },
    {
      title: <IntlMessages id="components.leavePolicyForm.approvalRequired" />,
      dataIndex: 'approvalRequired',
      key: 'approvalRequired',
      align: 'center' as const,
      render: (approvalRequired: boolean) => {
        if (approvalRequired) {
          return <IntlMessages id="app.yes" />
        } else {
          return <IntlMessages id="app.no" />
        }
      },
    },
    {
      title: <IntlMessages id="components.leavePolicyForm.negativeBalance" />,
      dataIndex: 'negativeBalance',
      key: 'negativeBalance',
      align: 'center' as const,
      render: (negativeBallanceAllowed: boolean) => {
        if (negativeBallanceAllowed) {
          return <IntlMessages id="app.yes" />
        } else {
          return <IntlMessages id="app.no" />
        }
      },
    },
    {
      title: <IntlMessages id="app.shortestLeaveDuration" />,
      dataIndex: 'shortestDuration',
      key: 'shortestDuration',
      render: (duration: string) => <IntlMessages id={`app.${duration}`} />,
    },
    {
      title: <IntlMessages id="app.actions" />,
      key: 'actions',
      render: (_, record: ILeavePolicyOverview) => (
        <Space>
          <Link to={`/app/settings/location/${record.locationId}/leave-policies/${record.leaveTypeId}`} onClick={() => handleEditClick(record, 'edit_icon_click')}>
            <FormOutlined />
          </Link>
        </Space>
      ),
    },
  ]

  if (loading) {
    return <CircularProgress />
  }

  return (
    <div className="main-content">
      <PageHeader title={<IntlMessages id="sidebar.leavePolicies" />} />

      <div style={{ marginBottom: 20 }}>
        <Text>
          <IntlMessages id="locations.leavePolicies.description" />
        </Text>
      </div>

      <div className="main-content-body">
        <Row style={{ marginBottom: 20, justifyContent: 'flex-end' }}>
          <Col>
            {!isInitialLoad &&
              (() => {
                const visibleLocations = isLocationAdmin && managedLocationIds.length > 0 ? locations.filter((l) => managedLocationIds.includes(l.id)) : locations
                const toOptions = <T extends { id: string; name: string }>(items: T[]) =>
                  [...items].sort((a, b) => a.name.localeCompare(b.name)).map((item) => ({ value: item.id, label: item.name }))
                const clearLabel = (name: string) => formatMessage({ id: 'users.table.clearFilter' }, { filter: name })
                const locationsLabel = formatMessage({ id: 'app.locations' })
                const leaveTypesLabel = formatMessage({ id: 'app.leaveTypes' })
                const locationsByCountry = locationOptionsByCountry(visibleLocations, formatMessage({ id: 'locationPlace.card.noCountrySet' }))
                const configs: FilterRowItem[] = [
                  {
                    id: 'locations',
                    label: locationsLabel,
                    options: locationsByCountry ?? toOptions(visibleLocations),
                    keepOrder: Boolean(locationsByCountry),
                    clearLabel: clearLabel(locationsLabel),
                  },
                  { id: 'leaveTypes', label: leaveTypesLabel, options: toOptions(leaveTypes), clearLabel: clearLabel(leaveTypesLabel) },
                ]
                const values: FilterValues = {
                  locations: filters.locationIds || [],
                  leaveTypes: filters.leaveTypeIds || [],
                }
                const onChange = (next: FilterValues) => handleFilterChange({ locationIds: next.locations || [], leaveTypeIds: next.leaveTypes || [] })
                return <FilterTriggerRow configs={configs} values={values} onChange={onChange} labels={rowLabels} />
              })()}
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredLeavePolicies}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content', y: 'calc(100vh - 400px)' }}
        />
      </div>
    </div>
  )
}

export default LeavePoliciesOverviewPage
